import { useEffect } from 'react';

import {
  useAppDispatch,
  useAppSelector,
  useScrollRestoration,
  useUserTimezone,
} from '@/shared/hooks';
import { groupByDay, trackEvent } from '@/shared/lib';

import {
  composedModel,
  useTranscriptorStartAvailability,
} from '@/entities/composed';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import { useOpenTemplatesPage } from '@/entities/templates';
import {
  sortConsultationsByDate,
  type TranscriptorConsultation,
  useOpenConsultation,
  useTranscriptorConsultations,
} from '@/entities/transcriptor';

import { useRecordingRowActions } from '@/widgets/RecordingRow';

import { RECORDINGS_SCROLL_KEY_PREFIX } from '../config';

const getConsultationDate = (consultation: TranscriptorConsultation) =>
  consultation.date;

export const usePatientRecordingsPage = (patientID: string) => {
  const dispatch = useAppDispatch();
  const userTimezone = useUserTimezone();
  const organizationId = useAppSelector(
    (state) =>
      patientModel.selectors.selectPatientByID(patientID)(state)
        ?.OrganizationID ?? '',
  );
  const transcriptorV2Enabled = useAppSelector((state) =>
    organizationModel.selectors.selectTranscriptorV2Enabled(
      state,
      organizationId,
    ),
  );

  const { CanRequestReport, CanViewStudies } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );
  const transcriptorAvailability = useAppSelector(
    composedModel.selectors.selectTranscriptorAvailability,
  );
  const canViewArchive = useAppSelector(
    composedModel.selectors.selectCanViewPatientRecordingsArchive(patientID),
  );

  const { consultations, hiddenConsultations, visibleTotal, isLoaded, error } =
    useTranscriptorConsultations(patientID, {
      transcriptorV2Enabled,
      includeHidden: canViewArchive,
    });
  const {
    actions: rowActions,
    hideMode,
    isConsultationStale,
  } = useRecordingRowActions();
  const openTemplatesPage = useOpenTemplatesPage();

  const {
    funding,
    canStartRecording,
    isStartRecordingDisabled,
    isStorageBlocked,
  } = useTranscriptorStartAvailability();

  const groups = groupByDay(
    sortConsultationsByDate(consultations),
    getConsultationDate,
  );
  const hiddenGroups = groupByDay(
    sortConsultationsByDate(hiddenConsultations),
    getConsultationDate,
  );

  const scrollKey = `${RECORDINGS_SCROLL_KEY_PREFIX}${patientID}`;
  const openRecording = useOpenConsultation({
    patientID,
    scrollKey,
    eventName: 'PatientRecordingsPageRecordingClicked',
  });

  const { saveScrollPosition } = useScrollRestoration({
    storageKey: scrollKey,
    isContentLoaded: isLoaded,
    maxAttempts: 10,
    attemptInterval: 200,
    timeoutMinutes: 5,
  });

  useEffect(() => {
    trackEvent({
      eventName: 'PatientRecordingsPageViewed',
      properties: { patient_id: patientID },
      isPageViewEvent: true,
    });
  }, [patientID]);

  const handleOpenTemplates = () => {
    saveScrollPosition();
    openTemplatesPage();
  };

  const handleStartRecording = () => {
    trackEvent({
      eventName: 'PatientRecordingsPageStartRecordingButtonClicked',
      properties: { patient_id: patientID },
    });

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.TranscriptorRecording,
        data: { patientID },
      }),
    );
  };

  return {
    groups,
    hiddenGroups,
    hiddenCount: hiddenConsultations.length,
    visibleTotal,
    isLoaded,
    hasLoadError: !isLoaded && Boolean(error),
    isEmpty: consultations.length === 0 && hiddenConsultations.length === 0,
    showHiddenSection: canViewArchive && hiddenConsultations.length > 0,
    hideMode,
    userTimezone,
    funding,
    canStartRecording: canStartRecording && CanRequestReport,
    isStartRecordingDisabled,
    isStorageBlocked,
    isConsultationStale,
    rowActions: { ...rowActions, onOpen: openRecording },
    handleStartRecording,
    // The Templates button needs Scribe, the same as recording.
    canOpenTemplates:
      transcriptorV2Enabled === true &&
      transcriptorAvailability.isReady &&
      transcriptorAvailability.isAvailable &&
      CanViewStudies,
    handleOpenTemplates,
  };
};
